import {CheckDetailsAlert, CheckInterval, LocationResponseTimes} from "@/lib/queries/checks.ts";
import {DataPoint} from "@/lib/types/checks.ts";
import {addMinutes, differenceInMinutes, parseISO, roundToNearestMinutes} from "date-fns";
import countBy from "lodash/countBy";
import maxBy from "lodash/maxBy";
import toPairs from "lodash/toPairs";

export const formatResponseTime = (time?: number, empty: string = "N/A"): string => {
  if (!time) {
    return empty;
  }

  if (time < 0.001) {
    return `${Number(time * 10 ** 6).toFixed(0)}µs`;
  }

  if (time < 1) {
    return `${Number(time * 10 ** 3).toFixed(0)}ms`;
  }

  return `${Number(time).toFixed(2)}s`;
};

export const findMode = (responses: number[]): number => {
  const mode = maxBy(
    toPairs(countBy(responses)).filter((p) => p[0] !== "0"),
    (p) => p[1]
  );
  return mode ? Number(mode[0]) : NaN;
};

export const getResponseInterval = (responseTimes: LocationResponseTimes, interval: number): number => {
  const sampleNumber = 30;
  const flattenPoints = (responseTimes || {data: []}).data
    .slice(0, sampleNumber)
    .map((p) => p.timestamp)
    .sort()
    .map((t) => parseISO(t));

  if (interval === 0 && flattenPoints.length < 2) {
    console.error("could not calculate average response time interval, setting to 15 minutes");
    const defaultIntervalMin = 15;
    return defaultIntervalMin;
  }

  const stepsMs: number[] = [];
  for (let i = 0; i < flattenPoints.length; i++) {
    const current = flattenPoints[i];
    const next = flattenPoints[i + 1];
    if (current && next) {
      stepsMs.push(next.valueOf() - current.valueOf());
    }
  }
  // https://en.wikipedia.org/wiki/Mode_(statistics)
  const mode = findMode(stepsMs);
  const stepModeMs = flattenPoints.length >= 2 && mode ? mode : undefined;
  return stepModeMs ? Math.ceil(stepModeMs / 60000) : interval;
};

export const getIntervalAlerts = (intervals: CheckInterval[]): CheckDetailsAlert[] => {
  return intervals
    .filter((i) => i.alerts.length > 0)
    .reduce((acc: CheckDetailsAlert[], item) => {
      return acc.concat(item.alerts);
    }, []);
};

/*
Ensures data range contains data value for every point in time
use default value if no data point provided
end date not included
 */
export const fillInGaps = (points: DataPoint[], interval: number, defaultValue: number | null = null) => {
  if (points.length < 1) {
    return [];
  }

  const series: Map<number, number | null> = new Map<number, number | null>();
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const nextP = points[i + 1];
    // smooth out seconds
    series.set(roundToNearestMinutes(parseISO(p.timestamp)).getTime(), p.value);
    if (!nextP) {
      break;
    }

    let currentDate = roundToNearestMinutes(parseISO(p.timestamp));
    const nextDate = roundToNearestMinutes(parseISO(nextP.timestamp));
    while (differenceInMinutes(nextDate, currentDate) > interval && interval > 0) {
      currentDate = addMinutes(currentDate, interval);
      series.set(currentDate.getTime(), defaultValue);
    }
  }

  return [...series.entries()].sort((a, b) => a[0] - b[0]);
};

export interface MinMaxAvg {
  min: number;
  max: number;
  avg: number;
}

export const getMinMaxAvg = (responses: LocationResponseTimes[]): MinMaxAvg => {
  if (responses.length < 1 || responses[0].data.length < 1) {
    return {
      min: 0,
      max: 0,
      avg: 0,
    };
  }

  let min = responses[0].data[0].value;
  let max = min;
  let sum = 0;

  let points = 0;
  responses.forEach((r) => {
    points += r.data.length;
    r.data.forEach((p) => {
      if (p.value < min) {
        min = p.value;
      }
      if (p.value > max) {
        max = p.value;
      }
      sum += p.value;
    });
  });

  return {
    min,
    max,
    avg: sum / points,
  };
};
